import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../utils/axios-config';
import { toast } from 'sonner';

/**
 * Admin Hooks
 * React Query hooks for admin portal functionality
 */

// ===================== DASHBOARD ===================== //

export function useAdminDashboardStats() {
  return useQuery({
    queryKey: ['admin', 'dashboard', 'stats'],
    queryFn: async () => {
      const { data } = await axiosInstance.get('/admin/dashboard/stats');
      return data;
    },
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // Refetch every minute
  });
}

// ===================== USER MANAGEMENT ===================== //

export function useAdminUsers(params = {}) {
  return useQuery({
    queryKey: ['admin', 'users', params],
    queryFn: async () => {
      const { data } = await axiosInstance.get('/admin/users', { params });
      return data;
    },
    keepPreviousData: true,
  });
}

export function useAdminUserDetails(userId) {
  return useQuery({
    queryKey: ['admin', 'user', userId],
    queryFn: async () => {
      const { data } = await axiosInstance.get(`/admin/users/${userId}`);
      return data;
    },
    enabled: !!userId,
  });
}

export function useWarnUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, reason, internalNotes }) => {
      const { data } = await axiosInstance.post(
        `/admin/users/${userId}/warn`,
        {
          reason,
          internalNotes,
        }
      );
      return data;
    },
    onSuccess: (data, variables) => {
      toast.success(data.message || 'Warning issued successfully');
      queryClient.invalidateQueries(['admin', 'users']);
      queryClient.invalidateQueries(['admin', 'user', variables.userId]);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to warn user');
    },
  });
}

export function useSuspendUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, reason, duration, internalNotes }) => {
      const { data } = await axiosInstance.post(
        `/admin/users/${userId}/suspend`,
        {
          reason,
          duration,
          internalNotes,
        }
      );
      return data;
    },
    onSuccess: (data, variables) => {
      toast.success(data.message || 'User suspended successfully');
      queryClient.invalidateQueries(['admin', 'users']);
      queryClient.invalidateQueries(['admin', 'user', variables.userId]);
      queryClient.invalidateQueries(['admin', 'dashboard']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to suspend user');
    },
  });
}

export function useUnsuspendUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, reason }) => {
      const { data } = await axiosInstance.post(
        `/admin/users/${userId}/unsuspend`,
        {
          reason,
        }
      );
      return data;
    },
    onSuccess: (data, variables) => {
      toast.success(data.message || 'Suspension lifted');
      queryClient.invalidateQueries(['admin', 'users']);
      queryClient.invalidateQueries(['admin', 'user', variables.userId]);
      queryClient.invalidateQueries(['admin', 'dashboard']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to lift suspension');
    },
  });
}

export function useBanUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, reason, internalNotes, hideContent }) => {
      const { data } = await axiosInstance.post(
        `/admin/users/${userId}/ban`,
        {
          reason,
          internalNotes,
          hideContent,
        }
      );
      return data;
    },
    onSuccess: (data, variables) => {
      toast.success(data.message || 'User banned successfully');
      queryClient.invalidateQueries(['admin', 'users']);
      queryClient.invalidateQueries(['admin', 'user', variables.userId]);
      queryClient.invalidateQueries(['admin', 'dashboard']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to ban user');
    },
  });
}

export function useUnbanUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, reason, restoreContent }) => {
      const { data } = await axiosInstance.post(
        `/admin/users/${userId}/unban`,
        {
          reason,
          restoreContent,
        }
      );
      return data;
    },
    onSuccess: (data, variables) => {
      toast.success(data.message || 'User unbanned successfully');
      queryClient.invalidateQueries(['admin', 'users']);
      queryClient.invalidateQueries(['admin', 'user', variables.userId]);
      queryClient.invalidateQueries(['admin', 'dashboard']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to unban user');
    },
  });
}

export function useChangeUserRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, newRole, reason }) => {
      const { data } = await axiosInstance.put(
        `/admin/users/${userId}/role`,
        {
          newRole,
          reason,
        }
      );
      return data;
    },
    onSuccess: (data, variables) => {
      toast.success(data.message || 'Role changed successfully');
      queryClient.invalidateQueries(['admin', 'users']);
      queryClient.invalidateQueries(['admin', 'user', variables.userId]);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to change role');
    },
  });
}

export function useUpdateUserProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, profileData, reason }) => {
      const { data } = await axiosInstance.put(
        `/admin/users/${userId}/profile`,
        {
          ...profileData,
          reason,
        }
      );
      return data;
    },
    onSuccess: (data, variables) => {
      toast.success(data.message || 'Profile updated successfully');
      queryClient.invalidateQueries(['admin', 'users']);
      queryClient.invalidateQueries(['admin', 'user', variables.userId]);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to update profile');
    },
  });
}

export function useRestrictProfileField() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, field, reason }) => {
      const { data } = await axiosInstance.post(
        `/admin/users/${userId}/restrict/${field}`,
        { reason }
      );
      return data;
    },
    onSuccess: (data, variables) => {
      toast.success(
        data.message || `${variables.field} restricted successfully`
      );
      queryClient.invalidateQueries(['admin', 'users']);
      queryClient.invalidateQueries(['admin', 'user', variables.userId]);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to restrict field');
    },
  });
}

export function useLiftProfileRestriction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, field, restoreOriginal }) => {
      const { data } = await axiosInstance.post(
        `/admin/users/${userId}/lift-restriction/${field}`,
        { restoreOriginal }
      );
      return data;
    },
    onSuccess: (data, variables) => {
      toast.success(data.message || `${variables.field} restriction lifted`);
      queryClient.invalidateQueries(['admin', 'users']);
      queryClient.invalidateQueries(['admin', 'user', variables.userId]);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to lift restriction');
    },
  });
}

export function useUpdateUserModeration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, moderationSettings, reason }) => {
      const { data } = await axiosInstance.put(
        `/admin/users/${userId}/moderation`,
        {
          ...moderationSettings,
          reason,
        }
      );
      return data;
    },
    onSuccess: (data, variables) => {
      toast.success(data.message || 'Moderation settings updated');
      queryClient.invalidateQueries(['admin', 'users']);
      queryClient.invalidateQueries(['admin', 'user', variables.userId]);
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.error || 'Failed to update moderation settings'
      );
    },
  });
}

// ===================== CONTENT MODERATION ===================== //

export function useAdminDiscussions(params = {}) {
  return useQuery({
    queryKey: ['admin', 'discussions', params],
    queryFn: async () => {
      const { data } = await axiosInstance.get(
        '/admin/content/discussions',
        { params }
      );
      return data;
    },
    keepPreviousData: true,
  });
}

export function useAdminDeletedDiscussions(params = {}) {
  return useQuery({
    queryKey: ['admin', 'deletedDiscussions', params],
    queryFn: async () => {
      const { data } = await axiosInstance.get(
        '/admin/content/discussions/deleted',
        { params }
      );
      return data;
    },
    keepPreviousData: true,
  });
}

export function useAdminRestoreDeletedDiscussion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ discussionOid, reason }) => {
      const { data } = await axiosInstance.post(
        `/admin/content/discussion/${discussionOid}/restore-deleted`,
        { reason }
      );
      return data;
    },
    onSuccess: () => {
      toast.success('Discussion restored successfully');
      queryClient.invalidateQueries(['admin', 'deletedDiscussions']);
      queryClient.invalidateQueries(['admin', 'discussions']);
      queryClient.invalidateQueries(['admin', 'dashboard']);
      queryClient.invalidateQueries(['forum', 'discussions']);
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.error || 'Failed to restore discussion'
      );
    },
  });
}

export function useHideContent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ entityType, entityId, reason, internalNotes }) => {
      const { data } = await axiosInstance.post(
        `/admin/content/${entityType}/${entityId}/hide`,
        { reason, internalNotes }
      );
      return data;
    },
    onSuccess: () => {
      toast.success('Content hidden successfully');
      queryClient.invalidateQueries(['admin', 'discussions']);
      queryClient.invalidateQueries(['admin', 'dashboard']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to hide content');
    },
  });
}

export function useRestoreContent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ entityType, entityId, reason }) => {
      const { data } = await axiosInstance.post(
        `/admin/content/${entityType}/${entityId}/restore`,
        { reason }
      );
      return data;
    },
    onSuccess: () => {
      toast.success('Content restored successfully');
      queryClient.invalidateQueries(['admin', 'discussions']);
      queryClient.invalidateQueries(['admin', 'dashboard']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to restore content');
    },
  });
}

export function useDeleteContent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ entityType, entityId, reason, internalNotes }) => {
      const { data } = await axiosInstance.delete(
        `/admin/content/${entityType}/${entityId}`,
        { data: { reason, internalNotes } }
      );
      return data;
    },
    onSuccess: () => {
      toast.success('Content deleted successfully');
      queryClient.invalidateQueries(['admin', 'discussions']);
      queryClient.invalidateQueries(['admin', 'dashboard']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to delete content');
    },
  });
}

// ===================== MEDIA MODERATION ===================== //

export function useAdminMedia(params = {}) {
  return useQuery({
    queryKey: ['admin', 'media', params],
    queryFn: async () => {
      const { data } = await axiosInstance.get('/admin/media', { params });
      return data;
    },
    keepPreviousData: true,
  });
}

export function useAdminMediaDetails(mediaId) {
  return useQuery({
    queryKey: ['admin', 'media', mediaId],
    queryFn: async () => {
      const { data } = await axiosInstance.get(`/admin/media/${mediaId}`);
      return data;
    },
    enabled: !!mediaId,
  });
}

export function useApproveMedia() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ mediaId, internalNotes }) => {
      const { data } = await axiosInstance.post(
        `/admin/media/${mediaId}/approve`,
        { internalNotes }
      );
      return data;
    },
    onSuccess: () => {
      toast.success('Media approved');
      queryClient.invalidateQueries(['admin', 'media']);
      queryClient.invalidateQueries(['admin', 'dashboard']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to approve media');
    },
  });
}

export function useHideMedia() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ mediaId, reason, internalNotes }) => {
      const { data } = await axiosInstance.post(
        `/admin/media/${mediaId}/hide`,
        { reason, internalNotes }
      );
      return data;
    },
    onSuccess: () => {
      toast.success('Media hidden');
      queryClient.invalidateQueries(['admin', 'media']);
      queryClient.invalidateQueries(['admin', 'dashboard']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to hide media');
    },
  });
}

export function useRemoveMedia() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ mediaId, reason, internalNotes }) => {
      const { data } = await axiosInstance.post(
        `/admin/media/${mediaId}/remove`,
        { reason, internalNotes }
      );
      return data;
    },
    onSuccess: () => {
      toast.success('Media removed permanently');
      queryClient.invalidateQueries(['admin', 'media']);
      queryClient.invalidateQueries(['admin', 'dashboard']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to remove media');
    },
  });
}

export function useRestoreMedia() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ mediaId, reason, url }) => {
      const { data } = await axiosInstance.post(
        `/admin/media/${mediaId}/restore`,
        { reason, url }
      );
      return data;
    },
    onSuccess: () => {
      toast.success('Media restored');
      queryClient.invalidateQueries(['admin', 'media']);
      queryClient.invalidateQueries(['admin', 'dashboard']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to restore media');
    },
  });
}

// ===================== REPORTS ===================== //

export function useAdminReports(params = {}) {
  return useQuery({
    queryKey: ['admin', 'reports', params],
    queryFn: async () => {
      const { data } = await axiosInstance.get('/admin/reports', {
        params,
      });
      return data;
    },
    keepPreviousData: true,
  });
}

export function useAdminReportDetails(reportId) {
  return useQuery({
    queryKey: ['admin', 'report', reportId],
    queryFn: async () => {
      const { data } = await axiosInstance.get(
        `/admin/reports/${reportId}`
      );
      return data;
    },
    enabled: !!reportId,
  });
}

export function useResolveReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ reportId, resolution, resolutionNotes }) => {
      const { data } = await axiosInstance.post(
        `/admin/reports/${reportId}/resolve`,
        {
          resolution,
          resolutionNotes,
        }
      );
      return data;
    },
    onSuccess: () => {
      toast.success('Report resolved successfully');
      queryClient.invalidateQueries(['admin', 'reports']);
      queryClient.invalidateQueries(['admin', 'dashboard']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to resolve report');
    },
  });
}

export function useDismissReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ reportId, reason }) => {
      const { data } = await axiosInstance.post(
        `/admin/reports/${reportId}/dismiss`,
        {
          reason,
        }
      );
      return data;
    },
    onSuccess: () => {
      toast.success('Report dismissed');
      queryClient.invalidateQueries(['admin', 'reports']);
      queryClient.invalidateQueries(['admin', 'dashboard']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to dismiss report');
    },
  });
}

export function useBulkDismissReports() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ reportIds, reason }) => {
      const { data } = await axiosInstance.post(
        '/admin/reports/bulk-dismiss',
        { reportIds, reason }
      );
      return data;
    },
    onSuccess: (data) => {
      toast.success(`${data.modifiedCount} reports dismissed`);
      queryClient.invalidateQueries(['admin', 'reports']);
      queryClient.invalidateQueries(['admin', 'dashboard']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to dismiss reports');
    },
  });
}

export function useBulkResolveReports() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ reportIds, resolution, resolutionNotes }) => {
      const { data } = await axiosInstance.post(
        '/admin/reports/bulk-resolve',
        { reportIds, resolution, resolutionNotes }
      );
      return data;
    },
    onSuccess: (data) => {
      toast.success(`${data.modifiedCount} reports resolved`);
      queryClient.invalidateQueries(['admin', 'reports']);
      queryClient.invalidateQueries(['admin', 'dashboard']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to resolve reports');
    },
  });
}

// ===================== AUDIT LOGS ===================== //

export function useAdminAuditLogs(params = {}) {
  return useQuery({
    queryKey: ['admin', 'audit-logs', params],
    queryFn: async () => {
      const { data } = await axiosInstance.get('/admin/audit-logs', {
        params,
      });
      return data;
    },
    keepPreviousData: true,
  });
}

// Alias for backwards compatibility
export const useAuditLogs = useAdminAuditLogs;

export function useModerationActions(params = {}) {
  return useQuery({
    queryKey: ['admin', 'moderation-actions', params],
    queryFn: async () => {
      const { data } = await axiosInstance.get(
        '/admin/moderation-actions',
        { params }
      );
      return data;
    },
    keepPreviousData: true,
  });
}

// ===================== USER REPORTS (Public) ===================== //

export function useCheckReport(entityType, entityId, entityOID) {
  return useQuery({
    queryKey: ['report-check', entityType, entityId, entityOID],
    queryFn: async () => {
      // Only include non-null params
      const params = { entityType };
      if (entityId) params.entityId = entityId;
      if (entityOID) params.entityOID = entityOID;

      const { data } = await axiosInstance.get('/omniverse/report/check', {
        params,
      });
      return data;
    },
    enabled: !!entityType && (!!entityId || !!entityOID),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

export function useSubmitReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      entityType,
      entityId,
      entityOID,
      reason,
      description,
      mediaUrl,
      mimeType,
      parentDiscussionId,
    }) => {
      const { data } = await axiosInstance.post('/omniverse/report', {
        entityType,
        entityId,
        entityOID,
        reason,
        description,
        mediaUrl,
        mimeType,
        parentDiscussionId,
      });
      return data;
    },
    onSuccess: (data, variables) => {
      toast.success(
        'Report submitted successfully. Thank you for helping keep our community safe.'
      );
      // Invalidate the check query so it updates
      queryClient.invalidateQueries([
        'report-check',
        variables.entityType,
        variables.entityId,
        variables.entityOID,
      ]);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to submit report');
    },
  });
}

export function useMyReports(params = {}) {
  return useQuery({
    queryKey: ['my-reports', params],
    queryFn: async () => {
      const { data } = await axiosInstance.get(
        '/omniverse/report/my-reports',
        { params }
      );
      return data;
    },
  });
}

// ===================== NODE HEALTH ===================== //

export function useNodeHealth() {
  return useQuery({
    queryKey: ['admin', 'node', 'health'],
    queryFn: async () => {
      const { data } = await axiosInstance.get('/admin/node/health');
      return data;
    },
    staleTime: 5 * 1000, // 5 seconds
    refetchInterval: 10 * 1000, // Refetch every 10 seconds for near real-time
  });
}

export function useWalletBalances() {
  return useQuery({
    queryKey: ['admin', 'node', 'wallet'],
    queryFn: async () => {
      const { data } = await axiosInstance.get('/admin/node/wallet');
      return data;
    },
    staleTime: 10 * 1000,
    refetchInterval: 30 * 1000, // Wallet balances don't change as frequently
  });
}

export function useSystemAlerts(params = {}) {
  return useQuery({
    queryKey: ['admin', 'node', 'alerts', params],
    queryFn: async () => {
      const { data } = await axiosInstance.get('/admin/node/alerts', {
        params,
      });
      return data;
    },
    staleTime: 5 * 1000,
    refetchInterval: 10 * 1000, // Check for new alerts every 10 seconds
  });
}

export function useAcknowledgeAlert() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (alertId) => {
      const { data } = await axiosInstance.post(
        `/admin/node/alerts/${alertId}/acknowledge`
      );
      return data;
    },
    onSuccess: () => {
      toast.success('Alert acknowledged');
      queryClient.invalidateQueries(['admin', 'node']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to acknowledge alert');
    },
  });
}

export function useResolveAlert() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (alertId) => {
      const { data } = await axiosInstance.post(
        `/admin/node/alerts/${alertId}/resolve`
      );
      return data;
    },
    onSuccess: () => {
      toast.success('Alert resolved');
      queryClient.invalidateQueries(['admin', 'node']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to resolve alert');
    },
  });
}

// ===================== COMMUNITY ADMIN ===================== //

// Get all communities for admin panel
export function useAdminCommunities(params = {}) {
  return useQuery({
    queryKey: ['admin', 'communities', params],
    queryFn: async () => {
      const { data } = await axiosInstance.get('/admin/communities', {
        params,
      });
      return data;
    },
    staleTime: 30 * 1000, // 30 seconds
  });
}

export function useAdminCommunityActions() {
  const queryClient = useQueryClient();

  const suspendCommunity = useMutation({
    mutationFn: async ({ communityOid, suspend, reason }) => {
      const { data } = await axiosInstance.post(
        `/admin/communities/${communityOid}/suspend`,
        { suspend, reason }
      );
      return data;
    },
    onSuccess: () => {
      toast.success('Community status updated');
      queryClient.invalidateQueries(['communities']);
      queryClient.invalidateQueries(['admin', 'communities']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to update community');
    },
  });

  const deleteCommunity = useMutation({
    mutationFn: async ({ communityOid, reason }) => {
      const { data } = await axiosInstance.delete(
        `/admin/communities/${communityOid}`,
        { data: { reason } }
      );
      return data;
    },
    onSuccess: () => {
      toast.success('Community deleted successfully');
      queryClient.invalidateQueries(['communities']);
      queryClient.invalidateQueries(['admin', 'communities']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to delete community');
    },
  });

  const updateCommunity = useMutation({
    mutationFn: async ({ communityOid, data }) => {
      const response = await axiosInstance.patch(
        `/admin/communities/${communityOid}`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      toast.success('Community updated successfully');
      queryClient.invalidateQueries(['communities']);
      queryClient.invalidateQueries(['admin', 'communities']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to update community');
    },
  });

  return {
    suspendCommunity: suspendCommunity.mutate,
    deleteCommunity: deleteCommunity.mutate,
    updateCommunity: updateCommunity.mutate,
    isLoading:
      suspendCommunity.isPending ||
      deleteCommunity.isPending ||
      updateCommunity.isPending,
  };
}

export function useCommunityAdminStats(communityOid) {
  return useQuery({
    queryKey: ['community-admin', communityOid, 'dashboard'],
    queryFn: async () => {
      const { data } = await axiosInstance.get(
        `/omniverse/communities/${communityOid}/admin/dashboard`
      );
      return data;
    },
    enabled: !!communityOid,
    staleTime: 30 * 1000, // 30 seconds
  });
}

export function useCommunityAdminMembers(communityOid, params = {}) {
  return useQuery({
    queryKey: ['community-admin', communityOid, 'members', params],
    queryFn: async () => {
      const { data } = await axiosInstance.get(
        `/omniverse/communities/${communityOid}/admin/members`,
        { params }
      );
      return data;
    },
    enabled: !!communityOid,
    keepPreviousData: true,
  });
}

export function useCommunityAdminPosts(communityOid, params = {}) {
  return useQuery({
    queryKey: ['community-admin', communityOid, 'posts', params],
    queryFn: async () => {
      const { data } = await axiosInstance.get(
        `/omniverse/communities/${communityOid}/admin/discussions`,
        { params }
      );
      return data;
    },
    enabled: !!communityOid,
    keepPreviousData: true,
  });
}

export function useCommunityAdminReports(communityOid, params = {}) {
  return useQuery({
    queryKey: ['community-admin', communityOid, 'reports', params],
    queryFn: async () => {
      const { data } = await axiosInstance.get(
        `/omniverse/communities/${communityOid}/admin/reports`,
        { params }
      );
      return data;
    },
    enabled: !!communityOid,
    keepPreviousData: true,
  });
}

// ===================== COMMUNITY ADMIN MUTATIONS ===================== //

export function useUpdateMemberRole(communityOid) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ memberOid, role }) => {
      const { data } = await axiosInstance.put(
        `/omniverse/communities/${communityOid}/admin/members/${memberOid}/role`,
        { newRole: role }
      );
      return data;
    },
    onSuccess: (data) => {
      toast.success(data.message || 'Member role updated successfully');
      queryClient.invalidateQueries([
        'community-admin',
        communityOid,
        'members',
      ]);
      queryClient.invalidateQueries([
        'community-admin',
        communityOid,
        'dashboard',
      ]);
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.error || 'Failed to update member role'
      );
    },
  });
}

export function useKickMember(communityOid) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ memberOid, reason }) => {
      const { data } = await axiosInstance.delete(
        `/omniverse/communities/${communityOid}/admin/members/${memberOid}`,
        { data: { reason } }
      );
      return data;
    },
    onSuccess: (data) => {
      toast.success(data.message || 'Member kicked successfully');
      queryClient.invalidateQueries([
        'community-admin',
        communityOid,
        'members',
      ]);
      queryClient.invalidateQueries([
        'community-admin',
        communityOid,
        'dashboard',
      ]);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to kick member');
    },
  });
}

export function useBanMember(communityOid) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ memberOid, reason }) => {
      const { data } = await axiosInstance.post(
        `/omniverse/communities/${communityOid}/admin/members/${memberOid}/ban`,
        { reason }
      );
      return data;
    },
    onSuccess: (data) => {
      toast.success(data.message || 'Member banned successfully');
      queryClient.invalidateQueries([
        'community-admin',
        communityOid,
        'members',
      ]);
      queryClient.invalidateQueries([
        'community-admin',
        communityOid,
        'dashboard',
      ]);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to ban member');
    },
  });
}

export function useUnbanMember(communityOid) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (memberOid) => {
      const { data } = await axiosInstance.post(
        `/omniverse/communities/${communityOid}/admin/members/${memberOid}/unban`
      );
      return data;
    },
    onSuccess: (data) => {
      toast.success(data.message || 'Member unbanned successfully');
      queryClient.invalidateQueries([
        'community-admin',
        communityOid,
        'members',
      ]);
      queryClient.invalidateQueries([
        'community-admin',
        communityOid,
        'dashboard',
      ]);
      queryClient.invalidateQueries([
        'community-admin',
        communityOid,
        'banned',
      ]);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to unban member');
    },
  });
}

export function useSuspendMember(communityOid) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ memberOid, reason, duration }) => {
      const { data } = await axiosInstance.post(
        `/omniverse/communities/${communityOid}/admin/members/${memberOid}/suspend`,
        { reason, duration }
      );
      return data;
    },
    onSuccess: (data) => {
      toast.success(data.message || 'Member suspended successfully');
      queryClient.invalidateQueries([
        'community-admin',
        communityOid,
        'members',
      ]);
      queryClient.invalidateQueries([
        'community-admin',
        communityOid,
        'dashboard',
      ]);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to suspend member');
    },
  });
}

export function useUnsuspendMember(communityOid) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (memberOid) => {
      const { data } = await axiosInstance.post(
        `/omniverse/communities/${communityOid}/admin/members/${memberOid}/unsuspend`
      );
      return data;
    },
    onSuccess: (data) => {
      toast.success(data.message || 'Member unsuspended successfully');
      queryClient.invalidateQueries([
        'community-admin',
        communityOid,
        'members',
      ]);
      queryClient.invalidateQueries([
        'community-admin',
        communityOid,
        'dashboard',
      ]);
      queryClient.invalidateQueries([
        'community-admin',
        communityOid,
        'suspended',
      ]);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to unsuspend member');
    },
  });
}

export function useHideDiscussion(communityOid) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ discussionOid, reason }) => {
      const { data } = await axiosInstance.post(
        `/omniverse/communities/${communityOid}/admin/discussions/${discussionOid}/hide`,
        { reason }
      );
      return data;
    },
    onSuccess: (data) => {
      toast.success(data.message || 'Discussion hidden successfully');
      queryClient.invalidateQueries(['community-admin', communityOid, 'posts']);
      queryClient.invalidateQueries(['forum']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to hide discussion');
    },
  });
}

export function useUnhideDiscussion(communityOid) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (discussionOid) => {
      const { data } = await axiosInstance.post(
        `/omniverse/communities/${communityOid}/admin/discussions/${discussionOid}/unhide`
      );
      return data;
    },
    onSuccess: (data) => {
      toast.success(data.message || 'Discussion unhidden successfully');
      queryClient.invalidateQueries(['community-admin', communityOid, 'posts']);
      queryClient.invalidateQueries(['forum']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to unhide discussion');
    },
  });
}

export function usePinDiscussion(communityOid) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (discussionOid) => {
      const { data } = await axiosInstance.post(
        `/omniverse/communities/${communityOid}/admin/discussions/${discussionOid}/pin`
      );
      return data;
    },
    onSuccess: (data) => {
      toast.success(data.message || 'Discussion pinned successfully');
      queryClient.invalidateQueries(['community-admin', communityOid, 'posts']);
      queryClient.invalidateQueries(['forum']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to pin discussion');
    },
  });
}

export function useUnpinDiscussion(communityOid) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (discussionOid) => {
      const { data } = await axiosInstance.post(
        `/omniverse/communities/${communityOid}/admin/discussions/${discussionOid}/unpin`
      );
      return data;
    },
    onSuccess: (data) => {
      toast.success(data.message || 'Discussion unpinned successfully');
      queryClient.invalidateQueries(['community-admin', communityOid, 'posts']);
      queryClient.invalidateQueries(['forum']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to unpin discussion');
    },
  });
}

export function useFreezeDiscussion(communityOid) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ discussionOid, reason }) => {
      const { data } = await axiosInstance.post(
        `/omniverse/communities/${communityOid}/admin/discussions/${discussionOid}/freeze`,
        { reason }
      );
      return data;
    },
    onSuccess: (data) => {
      toast.success(data.message || 'Discussion frozen successfully');
      queryClient.invalidateQueries(['community-admin', communityOid, 'posts']);
      queryClient.invalidateQueries(['forum']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to freeze discussion');
    },
  });
}

export function useUnfreezeDiscussion(communityOid) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (discussionOid) => {
      const { data } = await axiosInstance.post(
        `/omniverse/communities/${communityOid}/admin/discussions/${discussionOid}/unfreeze`
      );
      return data;
    },
    onSuccess: (data) => {
      toast.success(data.message || 'Discussion unfrozen successfully');
      queryClient.invalidateQueries(['community-admin', communityOid, 'posts']);
      queryClient.invalidateQueries(['forum']);
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.error || 'Failed to unfreeze discussion'
      );
    },
  });
}

export function useDeleteCommunityDiscussion(communityOid) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ discussionOid, reason }) => {
      const { data } = await axiosInstance.delete(
        `/omniverse/communities/${communityOid}/admin/discussions/${discussionOid}`,
        { data: { reason } }
      );
      return data;
    },
    onSuccess: (data) => {
      toast.success(data.message || 'Discussion deleted successfully');
      queryClient.invalidateQueries(['community-admin', communityOid, 'posts']);
      queryClient.invalidateQueries([
        'community-admin',
        communityOid,
        'dashboard',
      ]);
      queryClient.invalidateQueries(['forum']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to delete discussion');
    },
  });
}

export function useUpdateCommunitySettings(communityOid) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (settings) => {
      const { data } = await axiosInstance.put(
        `/omniverse/communities/${communityOid}/admin/settings`,
        settings
      );
      return data;
    },
    onSuccess: (data) => {
      toast.success(data.message || 'Community settings updated successfully');
      queryClient.invalidateQueries(['community', communityOid]);
      queryClient.invalidateQueries([
        'community-admin',
        communityOid,
        'dashboard',
      ]);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to update settings');
    },
  });
}

export function useTransferOwnership(communityOid) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (newOwnerOid) => {
      const { data } = await axiosInstance.post(
        `/omniverse/communities/${communityOid}/admin/transfer-ownership`,
        { newOwnerOid }
      );
      return data;
    },
    onSuccess: (data) => {
      toast.success(data.message || 'Ownership transferred successfully');
      queryClient.invalidateQueries(['community', communityOid]);
      queryClient.invalidateQueries(['community-admin', communityOid]);
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.error || 'Failed to transfer ownership'
      );
    },
  });
}

export function useCommunityActivityLog(communityOid, params = {}) {
  return useQuery({
    queryKey: ['community-admin', communityOid, 'activity', params],
    queryFn: async () => {
      const { data } = await axiosInstance.get(
        `/omniverse/communities/${communityOid}/admin/activity`,
        { params }
      );
      return data;
    },
    enabled: !!communityOid,
    keepPreviousData: true,
  });
}

// ===================== COMMUNITY REPORT MUTATIONS ===================== //

export function useResolveCommunityReport(communityOid) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ reportId, resolution, resolutionNotes }) => {
      const { data } = await axiosInstance.post(
        `/omniverse/communities/${communityOid}/admin/reports/${reportId}/resolve`,
        { resolution, resolutionNotes }
      );
      return data;
    },
    onSuccess: (data) => {
      toast.success(data.message || 'Report resolved successfully');
      queryClient.invalidateQueries([
        'community-admin',
        communityOid,
        'reports',
      ]);
      queryClient.invalidateQueries([
        'community-admin',
        communityOid,
        'dashboard',
      ]);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to resolve report');
    },
  });
}

export function useDismissCommunityReport(communityOid) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ reportId, reason }) => {
      const { data } = await axiosInstance.post(
        `/omniverse/communities/${communityOid}/admin/reports/${reportId}/dismiss`,
        { reason }
      );
      return data;
    },
    onSuccess: (data) => {
      toast.success(data.message || 'Report dismissed successfully');
      queryClient.invalidateQueries([
        'community-admin',
        communityOid,
        'reports',
      ]);
      queryClient.invalidateQueries([
        'community-admin',
        communityOid,
        'dashboard',
      ]);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to dismiss report');
    },
  });
}

// ===================== STORAGE ANALYTICS ===================== //

export function useStorageStats() {
  return useQuery({
    queryKey: ['admin', 'storage', 'stats'],
    queryFn: async () => {
      const { data } = await axiosInstance.get('/admin/storage/stats');
      return data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - storage doesn't change frequently
  });
}

// ===================== GLOBAL ADMIN COMMUNITY MEMBER MANAGEMENT ===================== //

export function useAdminCommunityMembers(communityOid, params = {}) {
  return useQuery({
    queryKey: ['admin', 'community', communityOid, 'members', params],
    queryFn: async () => {
      const { data } = await axiosInstance.get(
        `/admin/communities/${communityOid}/members`,
        { params }
      );
      return data;
    },
    enabled: !!communityOid,
    keepPreviousData: true,
  });
}

export function useAdminUpdateMemberRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ communityOid, userOid, role, reason }) => {
      const { data } = await axiosInstance.post(
        `/admin/communities/${communityOid}/members/${userOid}/role`,
        { role, reason }
      );
      return data;
    },
    onSuccess: (data, variables) => {
      toast.success(data.message || 'Member role updated');
      queryClient.invalidateQueries([
        'admin',
        'community',
        variables.communityOid,
        'members',
      ]);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to update role');
    },
  });
}

export function useAdminBanCommunityMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ communityOid, userOid, ban, reason }) => {
      const { data } = await axiosInstance.post(
        `/admin/communities/${communityOid}/members/${userOid}/ban`,
        { ban, reason }
      );
      return data;
    },
    onSuccess: (data, variables) => {
      toast.success(
        data.message || (variables.ban ? 'Member banned' : 'Member unbanned')
      );
      queryClient.invalidateQueries([
        'admin',
        'community',
        variables.communityOid,
        'members',
      ]);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to update ban status');
    },
  });
}

export function useAdminRemoveCommunityMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ communityOid, userOid, reason }) => {
      const { data } = await axiosInstance.delete(
        `/admin/communities/${communityOid}/members/${userOid}`,
        { data: { reason } }
      );
      return data;
    },
    onSuccess: (data, variables) => {
      toast.success(data.message || 'Member removed from community');
      queryClient.invalidateQueries([
        'admin',
        'community',
        variables.communityOid,
        'members',
      ]);
      queryClient.invalidateQueries(['admin', 'communities']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to remove member');
    },
  });
}
